import React, { useMemo, useState, createElement } from 'react';
import { Download, Search, Calendar, FileText } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { applicationRecords } from '../../data/reportMockData';
import { ApplicationRecord } from '../../types';
export function AllApplicationsReport() {
  const today = new Date().toISOString().split('T')[0];
  const monthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const [dateFrom, setDateFrom] = useState('2024-01-01');
  const [dateTo, setDateTo] = useState('2024-12-31');
  const [appliedFrom, setAppliedFrom] = useState('2024-01-01');
  const [appliedTo, setAppliedTo] = useState('2024-12-31');
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 15;
  const filtered = useMemo(() => {
    return applicationRecords.filter(r => r.date >= appliedFrom && r.date <= appliedTo);
  }, [appliedFrom, appliedTo]);
  const totalPages = Math.ceil(filtered.length / rowsPerPage);
  const paginated = filtered.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);
  const handleSearch = () => {
    setAppliedFrom(dateFrom);
    setAppliedTo(dateTo);
    setCurrentPage(1);
  };
  const handleDownload = () => {
    const headers = ['Date', 'Payer Name', 'Payer NID', 'Amount', 'Payment Method', 'Order Number', 'Transaction ID', 'Service Type', 'Delivery Type', 'Service Info'];
    const rows = filtered.map(r => [r.date, r.payerName, r.payerNid, r.amount, r.paymentMethod, r.orderNumber, r.transactionId, r.serviceType, r.deliveryType, r.serviceInfo]);
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], {
      type: 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `all-applications-${appliedFrom}-to-${appliedTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const deliveryBadgeColor = (type: string) => {
    if (type === 'Online') return 'bg-blue-100 text-blue-700';
    if (type === 'Office Counter') return 'bg-emerald-100 text-emerald-700';
    return 'bg-orange-100 text-orange-700';
  };
  return <div className="space-y-6" data-id="element-234">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-235">
        <div data-id="element-236">
          <h1 className="text-2xl font-bold text-slate-800" data-id="element-237">
            All Applications Report
          </h1>
          <p className="text-slate-500" data-id="element-238">
            Complete list of all application transactions
          </p>
        </div>
        <Button onClick={handleDownload} variant="secondary" data-id="element-239">
          <Download className="mr-2 h-4 w-4" data-id="element-240" /> Download CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-4" data-id="element-241">
        <div className="flex flex-wrap items-end gap-4" data-id="element-242">
          <div data-id="element-243">
            <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-244">
              From Date
            </label>
            <div className="relative" data-id="element-245">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-246" />
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-10 pl-9 pr-3 rounded-md border border-slate-300 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none" data-id="element-247" />
            </div>
          </div>
          <div data-id="element-248">
            <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-249">
              To Date
            </label>
            <div className="relative" data-id="element-250">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-251" />
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-10 pl-9 pr-3 rounded-md border border-slate-300 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none" data-id="element-252" />
            </div>
          </div>
          <Button onClick={handleSearch} data-id="element-253">
            <Search className="mr-2 h-4 w-4" data-id="element-254" /> Search
          </Button>
          <div className="ml-auto text-sm text-slate-500 self-center" data-id="element-255">
            <span className="font-semibold text-slate-800" data-id="element-256">
              {filtered.length}
            </span>{' '}
            records found
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden" data-id="element-257">
        <div className="overflow-x-auto" data-id="element-258">
          <table className="w-full text-sm text-left" data-id="element-259">
            <thead className="bg-slate-50 border-b border-slate-200" data-id="element-260">
              <tr data-id="element-261">
                {['Date', 'Payer Name', 'Payer NID', 'Amount (৳)', 'Payment Method', 'Order Number', 'Transaction ID', 'Service Type', 'Delivery Type', 'Service Info'].map(h => <th key={h} className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap" data-id="element-262">
                    {h}
                  </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-263">
              {paginated.length > 0 ? paginated.map(row => <tr key={row.id} className="hover:bg-slate-50 transition-colors" data-id="element-264">
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap" data-id="element-265">
                      {row.date}
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-900 whitespace-nowrap" data-id="element-266">
                      {row.payerName}
                    </td>
                    <td className="px-4 py-3 font-mono text-slate-600 text-xs" data-id="element-267">
                      {row.payerNid}
                    </td>
                    <td className="px-4 py-3 font-semibold text-slate-900" data-id="element-268">
                      ৳{row.amount}
                    </td>
                    <td className="px-4 py-3" data-id="element-269">
                      <span className="bg-violet-100 text-violet-700 text-xs font-medium px-2 py-0.5 rounded-full" data-id="element-270">
                        {row.paymentMethod}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-600 whitespace-nowrap" data-id="element-271">
                      {row.orderNumber}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-600 whitespace-nowrap" data-id="element-272">
                      {row.transactionId}
                    </td>
                    <td className="px-4 py-3" data-id="element-273">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${row.serviceType === 'Khatian' ? 'bg-teal-100 text-teal-700' : 'bg-amber-100 text-amber-700'}`} data-id="element-274">
                        {row.serviceType}
                      </span>
                    </td>
                    <td className="px-4 py-3" data-id="element-275">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${deliveryBadgeColor(row.deliveryType)}`} data-id="element-276">
                        {row.deliveryType}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600 text-xs max-w-[200px] truncate" title={row.serviceInfo} data-id="element-277">
                      {row.serviceInfo}
                    </td>
                  </tr>) : <tr data-id="element-278">
                  <td colSpan={10} className="px-4 py-12 text-center text-slate-400" data-id="element-279">
                    <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" data-id="element-280" />
                    No records found for the selected date range.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex flex-col sm:flex-row justify-between items-center gap-2 text-sm text-slate-500" data-id="element-281">
          <span data-id="element-282">
            Showing{' '}
            {Math.min((currentPage - 1) * rowsPerPage + 1, filtered.length)}–
            {Math.min(currentPage * rowsPerPage, filtered.length)} of{' '}
            {filtered.length}
          </span>
          <div className="flex items-center space-x-1" data-id="element-283">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-3 py-1 rounded border border-slate-300 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-id="element-284">
              Previous
            </button>
            {Array.from({
            length: Math.min(5, totalPages)
          }, (_, i) => {
            const page = i + 1;
            return <button key={page} onClick={() => setCurrentPage(page)} className={`px-3 py-1 rounded border transition-colors ${currentPage === page ? 'bg-emerald-600 text-white border-emerald-600' : 'border-slate-300 hover:bg-white'}`} data-id="element-285">
                    {page}
                  </button>;
          })}
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages || totalPages === 0} className="px-3 py-1 rounded border border-slate-300 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-id="element-286">
              Next
            </button>
          </div>
        </div>
      </div>
    </div>;
}